// packages/kb-view/src/frontmatter.ts
//
// LAT-1605 (LAT-1600 S5) — the ONE frontmatter parse+strip module. Every
// surface that has an opinion about where a KB page's YAML frontmatter ends and
// its Markdown body begins reads it from here:
//
//   - the content-law gate (`tools/scripts/help-content.test.ts`), which used
//     to carry its own `/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/` regex;
//   - the web content pane (`apps/web/src/lib/file-content.ts`);
//   - the docs worker (`apps/docs`, later slice), which server-renders the same
//     pages into a public HTML head.
//
// Gate and renderers therefore cannot disagree about what frontmatter IS: a
// change here reddens the gate and the renderer together, by construction.
//
// The rule is deliberately narrow and mirrors the backend scanner in
// apps/mcp/src/core/markdown/outline.ts:
//
//   * the text must START with `---\n` — a leading blank line, indentation, or
//     a CRLF fence (`---\r\n`) is NOT frontmatter;
//   * the closing fence is the first `\n---` at or after the opening fence;
//   * that closing line must END in a newline — a block that runs to EOF with
//     no trailing newline is left alone;
//   * a lone `---` thematic break with no closing fence is left alone (LAT-904).
//
// Anything that fails those tests is ordinary Markdown and is returned verbatim.
// This module NEVER produces markup: `source` is raw YAML text and `body` is
// raw Markdown text. Rendering (and therefore escaping) is the caller's job —
// which is what keeps a hostile `description:` value inert.

import { parseDocument } from "yaml";

/** A document split into its leading YAML frontmatter block and its body. */
export interface FrontmatterBlock {
  /** The raw YAML between the fences, or null when the text carries none. */
  source: string | null;
  /** The Markdown body with a leading frontmatter block removed. */
  body: string;
}

/**
 * Split a leading YAML frontmatter block (`---` … `---`) off a document.
 * Returns `{ source: null, body: text }` — the text verbatim — whenever the
 * document does not open with a complete, newline-terminated fenced block.
 */
export function parseFrontmatter(text: string): FrontmatterBlock {
  if (!text.startsWith("---\n")) return { source: null, body: text };
  const close = text.indexOf("\n---", 4);
  if (close === -1) return { source: null, body: text };
  const blockEnd = text.indexOf("\n", close + 4);
  if (blockEnd === -1) return { source: null, body: text };
  return { source: text.slice(4, close), body: text.slice(blockEnd + 1) };
}

/** The document body with any leading frontmatter block removed. */
export function stripFrontmatter(text: string): string {
  return parseFrontmatter(text).body;
}

/**
 * Parse a frontmatter block's YAML into a plain record, or null when the source
 * is absent, malformed, or not a mapping.
 *
 * Hardened the same way every other YAML read in the product is: merge keys
 * off, duplicate keys rejected, and aliases refused at materialization
 * (`maxAliasCount: 0`) so a frontmatter block can never expand into a
 * billion-laughs payload. Values come back as data — strings stay strings, so a
 * `description` holding quotes, angle brackets, or a `<script>` tag is inert
 * until a caller chooses to render it (and React escapes it when they do).
 */
export function parseFrontmatterFields(
  source: string | null,
): Record<string, unknown> | null {
  if (source === null) return null;
  try {
    const document = parseDocument(source, {
      merge: false,
      prettyErrors: false,
      uniqueKeys: true,
    });
    if (document.errors.length > 0) return null;
    const value: unknown = document.toJS({ maxAliasCount: 0 });
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
  } catch {
    return null;
  }
}
